/**
 * 知识库类型定义
 *
 * 定义知识库相关的TypeScript类型，供前端组件、服务和状态管理使用
 * 包括知识库配置、向量检索、知识图谱、同步日志等类型
 */

// ==================== 枚举定义 ====================

/** 同步类型 */
export enum SyncType {
  MANUAL = 'manual',
  AUTO = 'auto'
}

/** 同步状态 */
export enum SyncStatusEnum {
  SUCCESS = 'success',
  FAILED = 'failed',
  IN_PROGRESS = 'in_progress'
}

/** 知识库错误码 */
export enum KnowledgeErrorCode {
  EMBEDDING_API_ERROR = 'EMBEDDING_API_ERROR',
  CHROMA_ERROR = 'CHROMA_ERROR',
  NEO4J_ERROR = 'NEO4J_ERROR',
  SYNC_TIMEOUT = 'SYNC_TIMEOUT',
  SYNC_RATE_LIMIT = 'SYNC_RATE_LIMIT',
  INVALID_QUERY = 'INVALID_QUERY'
}

/** 检索参数约束常量 */
export const RETRIEVAL_CONSTRAINTS = {
  TOP_K_MIN: 1,
  TOP_K_MAX: 20,
  SIMILARITY_MIN: 0,
  SIMILARITY_MAX: 1,
  PAGE_SIZE_MAX: 100
} as const;

// ==================== 向量知识条目相关类型 ====================

/** 向量知识条目（后端返回结构） */
export interface VectorEntry {
  id: string;
  type: 'chapter' | 'setting' | 'material';
  chapter_id?: number;
  setting_id?: number;
  document_id?: string;
  name: string;
  num?: number;
  word_count?: number;
  level?: string;
  setting_type?: string;
  file_size?: number;
  has_vector: boolean;
  vectorization_status?: 'indexed' | 'indexing' | 'not_indexed';
  // AI 标签（5 维度）
  conflict_type?: string;
  relationship_dynamic?: string;
  emotional_arc?: string;
  narrative_function?: string;
  archetype?: string;
  // 文档状态（仅素材类型）
  status?: 'uploaded' | 'parsing' | 'slicing' | 'indexing' | 'completed' | 'failed' | 'unindexed';
  error_message?: string;
  // 素材类型（仅素材类型）
  material_type?: 'novel' | 'technique' | 'other';
}

/** 向量列表查询请求 */
export interface VectorListRequest {
  type?: 'chapter' | 'setting' | 'material' | 'all';
  page?: number;
  page_size?: number;
}

/** 向量列表查询响应（后端返回结构） */
export interface VectorListResponse {
  items: VectorEntry[];
  total: number;
  page: number;
  page_size: number;
}

// ==================== 知识图谱相关类型 ====================

/** 图节点（后端返回结构） */
export interface GraphNode {
  id: string;
  label: string;
  type: string;
  workspace_id?: number;
  bg?: string;
  border?: string;
  summary?: string;
  name?: string;
  description?: string;
  first_appearance?: number;
  mention_count?: number;
  /** cytoscape 格式完整数据，透传后端返回的完整 data 对象 */
  _cytoscapeData?: Record<string, unknown>;
  position?: {
    x: number;
    y: number;
  };
  classes?: string;
}

/** 图边（后端返回结构） */
export interface GraphEdge {
  id?: string;
  source: string;
  target: string;
  label?: string;
  fact?: string;
  since_chapter?: number;
  strength?: number;
  valid_at?: string;
  invalid_at?: string;
  valid_chapter?: number;
  invalid_chapter?: number;
}

/** 知识图谱数据 */
export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
  total: number;
}

/** 图谱数据查询请求 */
export interface GraphDataRequest {
  limit?: number;
  node_types?: ('角色' | '地点' | '物品' | '事件' | 'Character' | 'Location' | 'Item' | 'Event')[];
  chapter_num?: number;
  setting_id?: number;
}

// ==================== 同步相关类型 ====================

/** 同步日志 */
export interface SyncLog {
  id: number;
  workspace_id: number;
  sync_type: SyncType;
  status: SyncStatusEnum;
  chapters_synced: number;
  settings_synced: number;
  duration_ms: number | null;
  error_message: string | null;
  created_at: string;
}

/** 手动同步请求 */
export interface SyncManualRequest {
  sync_chapters?: boolean;
  sync_settings?: boolean;
  force_rebuild?: boolean;
}

/** 同步开始响应 */
export interface SyncStartedResponse {
  sync_id: string;
  started_at: string;
}

/** 同步进度响应 */
export interface SyncProgressResponse {
  sync_id: string;
  stage: 'chroma' | 'neo4j';
  progress: number;
  total: number;
  current: string;
  timestamp: string;
}

/** 同步完成响应 */
export interface SyncCompletedResponse {
  sync_id: string;
  chapters_synced: number;
  settings_synced: number;
  duration_ms: number;
  completed_at: string;
}

/** 同步失败响应 */
export interface SyncFailedResponse {
  sync_id: string;
  error: string;
  error_message: string;
  failed_at: string;
}

/** 同步日志查询请求 */
export interface SyncLogsRequest {
  page?: number;
  page_size?: number;
}

/** 同步日志查询响应 */
export interface SyncLogsResponse {
  logs: SyncLog[];
  total: number;
  page: number;
  page_size: number;
}

// ==================== Episode 构建任务相关 ====================

/** Episode 构建任务状态 */
export enum BuildTaskStatus {
  PENDING = 'pending',
  PREPARING = 'preparing',
  ENTITY_EXTRACTION = 'entity_extraction',
  DEDUPLICATION = 'deduplication',
  EDGE_TRANSLATION = 'edge_translation',
  RETRYING = 'retrying',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/** Episode 构建任务进度 */
export interface EpisodeBuildTask {
  task_id: string;
  workspace_id: number;
  chapter_id?: number;
  setting_id?: number;
  status: BuildTaskStatus | string;
  progress: number;
  stage?: string;
  stage_message?: string;
  entity_count?: number;
  edge_count?: number;
  duration_ms?: number;
  error_message?: string;
  created_at?: string;
  updated_at?: string;
}

/** Episode 构建任务开始响应 */
export interface EpisodeBuildStartResponse {
  task_id: string;
  status: string;
  message: string;
}

/** 构建阶段配置 */
export const BUILD_STAGES = [
  { key: 'preparing', label: '语义预处理', message: '正在进行语义分析与预处理...', progress: 5 },
  { key: 'entity_extraction', label: '智能实体抽取', message: '正在抽取知识实体与关系...', progress: 15 },
  { key: 'deduplication', label: '实体消歧融合', message: '正在进行实体消歧与知识融合...', progress: 85 },
  { key: 'edge_translation', label: '关系语义增强', message: '正在增强关系语义表达...', progress: 95 },
  { key: 'completed', label: '构建完成', message: '知识图谱构建完成', progress: 100 },
] as const;

// ==================== WebSocket消息类型 ====================

/** 手动同步消息 */
export interface KnowledgeSyncManualMessage {
  type: 'knowledge.sync.manual';
  workspace_id: number;
  data?: SyncManualRequest;
}

/** 向量列表查询消息 */
export interface KnowledgeVectorsListMessage {
  type: 'knowledge.vectors.list';
  workspace_id: number;
  filters?: VectorListRequest;
}

/** 图谱数据查询消息 */
export interface KnowledgeGraphDataMessage {
  type: 'knowledge.graph.data';
  workspace_id: number;
  data?: GraphDataRequest;
}

/** 同步日志查询消息 */
export interface KnowledgeSyncLogsMessage {
  type: 'knowledge.sync.logs';
  workspace_id: number;
  page?: number;
  page_size?: number;
}

/** 知识库WebSocket消息联合类型 */
export type KnowledgeWebSocketMessage =
  | KnowledgeSyncManualMessage
  | KnowledgeVectorsListMessage
  | KnowledgeGraphDataMessage
  | KnowledgeSyncLogsMessage;

/** 同步进度消息联合类型 */
export type KnowledgeSyncProgressMessage =
  | { type: 'knowledge.sync.started'; data: SyncStartedResponse }
  | { type: 'knowledge.sync.progress'; data: SyncProgressResponse }
  | { type: 'knowledge.sync.completed'; data: SyncCompletedResponse }
  | { type: 'knowledge.sync.failed'; data: SyncFailedResponse };

// ==================== 类型守卫函数 ====================

/** 将未知类型断言为对象的辅助函数 */
function _isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * 通用枚举验证函数
 * @param value - 待验证的值
 * @param enumObj - 枚举对象
 * @returns 是否为有效的枚举值
 */
function _isValidEnum<T extends Record<string, string>>(value: unknown, enumObj: T): value is T[keyof T] {
  return typeof value === 'string' && Object.values(enumObj).includes(value as T[keyof T]);
}

/**
 * 验证同步类型是否有效
 * @param value - 待验证的值
 * @returns 是否为有效的同步类型
 */
export function isValidSyncType(value: unknown): value is SyncType {
  return _isValidEnum(value, SyncType);
}

/**
 * 验证同步状态是否有效
 * @param value - 待验证的值
 * @returns 是否为有效的同步状态
 */
export function isValidSyncStatus(value: unknown): value is SyncStatusEnum {
  return _isValidEnum(value, SyncStatusEnum);
}

/**
 * 验证向量类型是否有效
 * @param value - 待验证的值
 * @returns 是否为有效的向量类型
 */
export function isValidVectorType(value: unknown): value is 'chapter' | 'setting' | 'material' {
  return typeof value === 'string' && ['chapter', 'setting', 'material'].includes(value);
}

/**
 * 验证节点类型是否有效
 * @param value - 待验证的值
 * @returns 是否为有效的节点类型
 */
export function isValidNodeType(value: unknown): value is string {
  return typeof value === 'string' && ['Character', 'Location', 'Item', 'Event', '角色', '地点', '物品', '事件'].includes(value);
}

/**
 * 验证错误码是否有效
 * @param value - 待验证的值
 * @returns 是否为有效的知识库错误码
 */
export function isValidKnowledgeErrorCode(value: unknown): value is KnowledgeErrorCode {
  return _isValidEnum(value, KnowledgeErrorCode);
}

/**
 * 验证是否为知识库WebSocket消息
 * @param message - 待验证的消息
 * @returns 是否为有效的知识库WebSocket消息
 */
export function isKnowledgeWebSocketMessage(message: unknown): message is KnowledgeWebSocketMessage {
  if (!_isObject(message)) return false;
  const validTypes = ['knowledge.sync.manual', 'knowledge.vectors.list', 'knowledge.graph.data', 'knowledge.sync.logs'];
  return validTypes.includes(message.type as string);
}

/**
 * 验证是否为同步进度消息
 * @param message - 待验证的消息
 * @returns 是否为有效的同步进度消息
 */
export function isKnowledgeSyncProgressMessage(message: unknown): message is KnowledgeSyncProgressMessage {
  const validTypes = ['knowledge.sync.started', 'knowledge.sync.progress', 'knowledge.sync.completed', 'knowledge.sync.failed'];

  if (!_isObject(message)) return false;
  if (!validTypes.includes(message.type as string)) return false;
  if (message.data === undefined || message.data === null) return false;
  if (!_isObject(message.data)) return false;
  if (!('sync_id' in message.data)) return false;
  if (typeof message.data.sync_id !== 'string') return false;

  return true;
}

/**
 * 验证分页参数是否有效
 * @param params - 待验证的分页参数
 * @returns 是否为有效的分页参数
 */
export function isValidPaginationParams(params: unknown): params is { page: number; page_size: number } {
  if (!_isObject(params)) {
    return false;
  }

  const page = params.page;
  const pageSize = params.page_size;

  return (
    (page === undefined || (typeof page === 'number' && page >= 1)) &&
    (pageSize === undefined || (typeof pageSize === 'number' && pageSize >= 1 && pageSize <= RETRIEVAL_CONSTRAINTS.PAGE_SIZE_MAX))
  );
}